import React, { useState, useMemo } from 'react';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { setPopup, openCollectOverlay } from '@src/state/popupAtoms';
import { EPopup } from '@src/types/common';
import { ApiError } from '@src/services/api';
import { AddToCollection } from '@src/types/Popups';
import { CollectionView } from 'common';
import { useMutationCollectionCreate } from '@src/queries/collections/useMutationCollectionCreate';
import {
  useMutationCollectionAddMedia,
  useMutationCollectionUpdateQuery,
} from '@src/queries/collections/useMutationCollection';
import { getCollectionLangError } from '@src/pages/Collection/collectionErrors';
import TextContent from '@src/components/Media/TextContent';
import { MediaType } from '@adalong/schemas';
import { Loader } from '@src/components/Loader';
import { collectionCategories } from '@src/pages/Collection/List/consts';
import { Autocomplete, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { ADALONG_COLORS } from '@src/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAddAlert } from '@src/fragments/Alerting/alertAtom';
import { MediaImage } from '../../../../../../components/MediaImage';
import { COLLECTION_NAME_MAX_LENGTH } from '../../../../../../config';
import { useAtomValue } from 'jotai';
import { getFilter } from '@src/pages/Discover/components/Selectors/Selectors';
import { isMySelectionSectionFilled } from '@src/utils/QS';
import { MediaFilterBuilder } from '@src/fragments/Filters/utils/MediaFilterBuilder';
import { FacetState } from '@src/pages/Discover/components/Atoms/Atoms';
import { AppliedFilters } from '../AppliedFilters';
import { classes } from 'typestyle';
import {
  collectFrameClass,
  collectFrameLoadingClass,
  collectFrameLeftClass,
  collectFrameRightClass,
  collectFrameTitleClass,
  collectFrameRemoveClass,
  inputsClass,
  inputClass,
  buttonsClass,
  buttonBorderClass,
  buttonPrimaryClass,
  loaderCenteredClass,
} from '../sharedStyles';
import { style } from 'typestyle';
import { mergeFilterAndSearchToMQL } from './mergeFilterAndSearchToMQL';

interface OwnProps {
  media?: AddToCollection;
  isAutoCollection?: boolean;
  onClose: () => void;
  onCreated?: (collection: CollectionView) => void;
}

type IProps = OwnProps;

interface IState {
  error: ApiError | null;
  loading: boolean;
}

function CreateCollection(props: IProps) {
  const { isAutoCollection } = props;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('My selection');
  const [matchType, setMatchType] = useState<'partial' | 'exact'>('partial');
  const addAlert = useAddAlert();

  const facetState = useAtomValue(FacetState);
  const filterState = useAtomValue(getFilter);

  const isFilterEmpty = !isMySelectionSectionFilled(filterState);

  // Rebuild the filter based on the matchType selection
  const filterBuilt = useMemo(() => {
    const isExactSearch = matchType === 'exact';
    return MediaFilterBuilder.buildMediaFilter({
      ...facetState.mediaConstraints,
      isExactSearch,
    }, { blacklistedCreators: true });
  }, [facetState.mediaConstraints, matchType]);

  const {
    mutateAsync: createCollection,
    isPending: isCreating,
    reset: resetCreate,
  } = useMutationCollectionCreate();
  const {
    mutateAsync: addMedias,
    isPending: isAdding,
    reset: resetAddMedia,
  } = useMutationCollectionAddMedia();
  const {
    mutateAsync: updateCollectionQuery,
    isPending: isUpdating,
    reset: resetUpdateCollectionQuery,
  } = useMutationCollectionUpdateQuery();

  const clearForm = () => {
    setName('');
    setDescription('');
    setCategory('My selection');
    setMatchType('partial');
  };

  const closeOverlay = () => {
    clearForm();
    props.onClose();
  };

  const handleSuccessfulCreation = (collection: CollectionView) => {
    props.onCreated?.(collection);
    closeOverlay();
    appHelpers.addAlert({ text: window.T('collect.collectoverlay.success'), type: 'success' });
  };

  const onCreate = () => {
    const mediaIds = props.media?.map((m) => m._id) || [];
    createCollection(
      {
        name,
        description,
        category,
      },
      {
        onSuccess: (collection) => {
          if (mediaIds.length === 0 && !isAutoCollection) {
            handleSuccessfulCreation(collection);
            return;
          }

          if (mediaIds.length > 0) {
            addMedias(
              {
                collectionId: collection._id,
                mediaIds,
              },
              {
                onSuccess: () => handleSuccessfulCreation(collection),
                onError: (error) =>
                  addAlert({
                    text: getCollectionLangError(error),
                    close: resetAddMedia,
                  }),
              },
            ).catch(console.error);
          }

          if (isAutoCollection && !isFilterEmpty) {
            const mqlData = mergeFilterAndSearchToMQL(filterBuilt);
            
            updateCollectionQuery(
              { collectionId: collection._id, query: mqlData },
              {
                onSuccess: () => handleSuccessfulCreation(collection),
                onError: (error) =>
                  addAlert({
                    text: getCollectionLangError(error),
                    close: resetUpdateCollectionQuery,
                  }),
              },
            ).catch(console.error);
          }
        },
        onError: (error) =>
          addAlert({
            text: getCollectionLangError(error),
            close: resetCreate,
          }),
      },
    ).catch(console.error);
  };

  const isLoading = isCreating || isAdding || isUpdating;

  const loadingClassName = isLoading ? collectFrameLoadingClass : '';
  return (
    <div className={scrollContainerClass}>
      {loadingClassName ? (
        <div className={loaderCenteredClass}>
          <Loader size={40} />
        </div>
      ) : null}
      <div className={classes(createCollectionClass, collectFrameClass, loadingClassName)}>
        {props.media && props.media.length > 0 ? (
          <div className={collectFrameLeftClass} >
            <MediaImage src={props.media[0]?.cdn_image} style={{width: "100%"}} />
          </div>
        ) : null}
        {props.media && props.media.length > 0 && props.media[0].type === MediaType.Text ? (
          <TextContent caption={props.media[0].caption} />
        ) : null}
        <div className={collectFrameRightClass}>
          <PerfectScrollbar className={perfectScrollbarClass} options={{ suppressScrollX: true }}>
            <div style={{position: "relative"}}>
              {props.media ? (
                <div className={collectFrameTitleClass}>
                  {isAutoCollection ? window.T('collect.collectoverlay.create.auto_collection.title') : window.T('collect.collectoverlay.create.title')}
                  <div className={collectFrameRemoveClass} onClick={closeOverlay}>
                    <i className='material-icons notranslate'>close</i>
                  </div>
                </div>
              ) : null}
              {!props.media ? (
                <Typography fontFamily={'Public Sans, sans-serif'} fontSize={19}>
                  {isAutoCollection ? window.T('collect.collectoverlay.create.auto_collection.title') : window.T('collect.collectoverlay.create.title')}
                </Typography>
              ) : null}

              {isAutoCollection ? (
                <div className={subtitleClass}>
                  {window.T('collect.collectoverlay.create.auto_collection.desc')}
                </div>
              ) : null}

              <div className={inputsClass}>
                <div>
                  <input
                    className={inputClass}
                    name='collection-name'
                    placeholder='Name'
                    maxLength={COLLECTION_NAME_MAX_LENGTH}
                    value={name}
                    onChange={({ target }) => setName(target.value)}
                  />
                </div>
                <div>
                  <input
                    className={inputClass}
                    name='collection-description'
                    placeholder='Description (optional)'
                    value={description}
                    onChange={({ target }) => setDescription(target.value)}
                  />
                </div>
                <div>
                  <Autocomplete
                    className={inputClass}
                    options={collectionCategories}
                    id='disable-clearable'
                    disableClearable
                    popupIcon={<KeyboardArrowDownIcon sx={{ color: ADALONG_COLORS.LIGHT_WHITE }} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                        placeholder={'Category'}
                        sx={{
                          input: {
                            color: ADALONG_COLORS.LIGHT_GRAY,
                            borderColor: 'white',
                            '&::placeholder': {
                              color: ADALONG_COLORS.LIGHT_WHITE,
                            },
                          },
                        }}
                      />
                    )}
                    onChange={(_, value) => setCategory(value)}
                  />
                </div>
              </div>
            </div>

            {isAutoCollection && !isFilterEmpty ? (
              <AppliedFilters 
                matchType={matchType}
                onMatchTypeChange={setMatchType}
              />
            ) : null}

            <div className={buttonsClass}>
              <button className={classes('button', buttonBorderClass)} onClick={closeOverlay}>
                {window.T('collect.collectoverlay.create.cancel').toUpperCase()}
              </button>
              <button className={classes('button', buttonPrimaryClass)} onClick={onCreate}>
                {window.T('collect.collectoverlay.create.add').toUpperCase()}
              </button>
            </div>
          </PerfectScrollbar>
        </div>
      </div>
    </div>
  );
}

export default CreateCollection;

const createCollectionClass = style({});
const subtitleClass = style({ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: '4px 0 16px 0' });
const scrollContainerClass = style({
  position: 'relative',
});

const perfectScrollbarClass = style({
  maxHeight: 'calc(90vh - 20px)',
  paddingRight: 16,
  marginRight: -16,
});
